import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from "react-router-dom";

import Login from "./components/Login";
import LandingPage from "./components/LandingPage";
import PropertyAssistantDock from "./components/assistant/PropertyAssistantDock";
import UsageTracker from "./components/UsageTracker";
import AppErrorBoundary from "./components/ui/AppErrorBoundary";
import PageState from "./components/ui/PageState";
import RouteAccessibility from "./components/ui/RouteAccessibility";


import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import { clearStoredLoginMode, getStoredLoginMode } from "./utils/loginMode";
import { getUserDocument } from "./utils/firestoreReadDeduper";

import { useProperty } from "./context/PropertyContext";
import { PageAssistantProvider } from "./context/PageAssistantContext";

const Dashboard = lazy(() => import("./components/Dashboard"));
const InspectionsHub = lazy(() => import("./pages/InspectionsHub"));
const InspectionSpecRouter = lazy(() => import("./pages/InspectionSpecRouter"));
const LeasesHub = lazy(() => import("./pages/LeasesHub"));
const LeasesSpecRouter = lazy(() => import("./pages/LeasesSpecRouter"));
const WorkOrdersHub = lazy(() => import("./pages/WorkOrdersHub"));
const WorkOrdersSpecRouter = lazy(() => import("./pages/WorkOrdersSpecRouter"));
const FinancialsHub = lazy(() => import("./pages/FinancialsHub"));
const FinancialsSpecRouter = lazy(() => import("./pages/FinancialsSpecRouter"));
const EntityFinancialsPage = lazy(() => import("./pages/EntityFinancialsPage"));
const ReportsHub = lazy(() => import("./pages/ReportsHub"));
const ReportsSpecRouter = lazy(() => import("./pages/ReportsSpecRouter"));
const RentRollReport = lazy(() => import("./pages/RentRollReport"));
const RentRollT12Report = lazy(() => import("./pages/RentRollT12Report"));
const AutomationExceptionsDashboard = lazy(() => import("./pages/AutomationExceptionsDashboard"));
const ETransferAutomationPage = lazy(() => import("./pages/ETransferAutomationPage"));
const NoticesHub = lazy(() => import("./pages/NoticesHub"));
const PropertyAssistant = lazy(() => import("./pages/PropertyAssistant"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const PropertySettingsHub = lazy(() => import("./pages/PropertySettingsHub"));
const PropertyUsersSettings = lazy(() => import("./pages/PropertyUsersSettings"));
const AutoEmailSettings = lazy(() => import("./pages/AutoEmailSettings"));
const PropertySettingsSuites = lazy(() => import("./pages/PropertySettingsSuites"));
const PropertyInspectionVisibilitySettings = lazy(() => import("./pages/PropertyInspectionVisibilitySettings"));
const SuiteEditor = lazy(() => import("./pages/SuiteEditor"));
const PropertySettingsSpecRouter = lazy(() => import("./pages/PropertySettingsSpecRouter"));
const PropertyTenantInformation = lazy(() => import("./pages/PropertyTenantInformation"));
const PropertyDefaultLeaseSetup = lazy(() => import("./pages/PropertyDefaultLeaseSetup"));
const MarketingHub = lazy(() => import("./pages/MarketingHub"));
const InspectionForm = lazy(() => import("./components/InspectionForm"));
const QuarterlyInspectionForm = lazy(() => import("./components/QuarterlyInspectionForm"));
const AnnualInspectionForm = lazy(() => import("./components/AnnualInspectionForm"));
const SpringChecklist = lazy(() => import("./components/SpringChecklist"));
const FallChecklist = lazy(() => import("./components/FallChecklist"));
const MoveOutChecklist = lazy(() => import("./components/MoveOutChecklist"));
const MoveInChecklist = lazy(() => import("./components/MoveInChecklist"));
const MonthlyLeaseAgreement = lazy(() => import("./components/MonthlyLeaseAgreement"));
const FixedLeaseAgreement = lazy(() => import("./components/FixedLeaseAgreement"));
const LeaseOverview = lazy(() => import("./components/LeaseOverview"));
const InspectionTracker = lazy(() => import("./components/InspectionTracker"));
const WorkOrdersList = lazy(() => import("./components/WorkOrdersList"));
const WorkOrderDetail = lazy(() => import("./components/WorkOrderDetail"));
const CreateWorkOrderPage = lazy(() => import("./components/CreateWorkOrderPage"));
const RentCollectionDashboard = lazy(() => import("./components/RentCollectionDashboard"));
const ExpenseDashboard = lazy(() => import("./components/ExpenseDashboard"));
const RevenueDashboard = lazy(() => import("./components/RevenueDashboard"));
const MetricsDashboard = lazy(() => import("./components/MetricsDashboard"));
const YearlySummaryDashboard = lazy(() => import("./components/YearlySummaryDashboard"));
const TenantLogin = lazy(() => import("./components/TenantLogin"));
const TenantDashboard = lazy(() => import("./components/TenantDashboard"));
const TenantMessagesInbox = lazy(() => import("./pages/TenantMessagesInbox"));
const Assessment = lazy(() => import("./components/Assessment"));
const PropertySelector = lazy(() => import("./pages/PropertySelector"));
const StatisticsPage = lazy(() => import("./pages/StatisticsPage"));
const OntarioLease = lazy(() => import("./components/ontariolease"));
const ManitobaLease = lazy(() => import("./components/manitobalease"));
const AlbertaLease = lazy(() => import("./components/albertalease"));
const DocumentSignPage = lazy(() => import("./pages/DocumentSignPage"));
const OntarioLeaseSignPage = lazy(() => import("./pages/OntarioLeaseSignPage"));
const ManitobaLeaseSignPage = lazy(() => import("./pages/ManitobaLeaseSignPage"));
const LeaseSignPage = lazy(() => import("./pages/LeaseSignPage"));
const LeaseImportPage = lazy(() => import("./pages/LeaseImportPage"));
const VendorsPage = lazy(() => import("./components/VendorsPage"));
const RenameSuites = lazy(() => import("./components/RenameSuites"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));

/* =====================================================
   STAFF ROLES — allowed into manager application
   ===================================================== */
const STAFF_ROLES = [
  "manager",
  "assistant",
  "accounting",
  "maintenance",
  "viewer",
];

function App() {
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [identityRevision, setIdentityRevision] = useState(0);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [hasTenantAccess, setHasTenantAccess] = useState(false);
  const [loginMode, setLoginMode] = useState(() => getStoredLoginMode());

  const { activeProperty, hasPermission, accessReady } = useProperty();
  const assistantDockEnabled = loggedIn && loginMode !== "tenant" && STAFF_ROLES.includes(userRole);
  const refreshSession = (mode) => {
    if (mode) setLoginMode(mode);
    setAuthReady(false);
    setIdentityRevision(value => value + 1);
  };
  const pendingSession = !authReady
    ? <PageState title="Restoring your session" loading />
    : authError
    ? <PageState title="We could not load your account" onRetry={() => refreshSession()}>Check your connection and try again.</PageState>
    : null;

  /* -----------------------------------------------------
     AUTH + ROLE RESOLUTION
     ----------------------------------------------------- */
  useEffect(() => {
    let authRevision = 0;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const revision = ++authRevision;
      setAuthError(false);
      setAuthReady(false);
      setUserRole(null);
      setHasTenantAccess(false);

      if (!user) {
        setLoggedIn(false);
        setUserRole(null);
        setHasTenantAccess(false);
        setLoginMode(clearStoredLoginMode());
        setAuthReady(true);
        return;
      }

      setLoggedIn(true);

      try {
        const userDoc = await getUserDocument(db, user.uid);
        if (revision !== authRevision) return;

        const userData = userDoc.data() || {};
        const role = String(userData?.role || "").trim().toLowerCase();
        const tenantAccessEnabled =
          String(role || "").toLowerCase() === "tenant" ||
          userData?.tenantAccess?.enabled === true;
        setUserRole(role || null);
        setHasTenantAccess(tenantAccessEnabled);
        setLoginMode((currentMode) => {
          const storedMode = getStoredLoginMode();
          if (storedMode) return storedMode;
          if (tenantAccessEnabled && !STAFF_ROLES.includes(role)) return "tenant";
          if (STAFF_ROLES.includes(role)) return "staff";
          return currentMode || "";
        });
      } catch (e) {
        if (revision !== authRevision) return;
        console.error("[App] Failed loading user role", e);
        setUserRole(null);
        setHasTenantAccess(false);
        setAuthError(true);
      } finally {
        if (revision === authRevision) setAuthReady(true);
      }
    });

    return () => {
      authRevision += 1;
      unsubscribe();
    };
  }, [identityRevision]);

  /* -----------------------------------------------------
     ROUTE GUARD — staff + property + permissions
     ----------------------------------------------------- */
  const requireStaffWithProperty = (component, requiredPermission = null) => {
    if (pendingSession) return pendingSession;
    if (!loggedIn) return <Navigate to="/manager-login" />;
    if (loginMode === "tenant") {
      return hasTenantAccess ? <Navigate to="/tenant-dashboard" /> : <Navigate to="/" />;
    }
    if (!STAFF_ROLES.includes(userRole)) return <Navigate to="/" />;
    if (!activeProperty) return <Navigate to="/select-property" />;
    if (!accessReady) {
      return <PageState title="Loading property access" loading />;
    }

    // Hidden-by-permission routing (no forbidden page)
    if (requiredPermission?.key) {
      const min = requiredPermission.min || "read";

      const ok =
        min === "readOrWrite"
          ? (hasPermission(requiredPermission.key, "read") ||
              hasPermission(requiredPermission.key, "write"))
          : hasPermission(requiredPermission.key, min);

      if (!ok) return <PageState title="Access is restricted" action={<Link to="/dashboard" className="app-button-secondary">Return to dashboard</Link>}>Your account does not have access to this feature for the selected property.</PageState>;
    }

    return component;
  };

  return (
    <Router>
      <PageAssistantProvider>
        <div className="App">
          <a href="#main-content" className="app-skip-link">Skip to main content</a>
          <RouteAccessibility />
          <UsageTracker enabled={loggedIn} userRole={userRole || ""} loginMode={loginMode || ""} />
          <main id="main-content" tabIndex={-1}>
          <AppErrorBoundary>
          <Suspense fallback={<PageState title="Loading page" loading />}>
          <Routes>
          {/* ================= PUBLIC ================= */}
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/tenant-login"
            element={<TenantLogin onLogin={(mode) => refreshSession(mode || "tenant")} />}
          />
<Route
  path="/invite"
  element={<InviteAccept onLogin={(mode) => refreshSession(mode)} />}
/>

          {/* ================= MANAGER LOGIN ================= */}
          <Route
            path="/manager-login"
            element={
              pendingSession || (loggedIn && STAFF_ROLES.includes(userRole) && loginMode !== "tenant"
                ? <Navigate to="/select-property" />
                : <Login onLogin={(mode) => refreshSession(mode || "staff")} />)
            }
          />

          {/* ================= PROPERTY SELECTOR ================= */}
          <Route
            path="/select-property"
            element={
              pendingSession || (loggedIn && STAFF_ROLES.includes(userRole)
                && loginMode !== "tenant"
                ? <PropertySelector />
                : <Navigate to="/manager-login" />)
            }
          />
          <Route
            path="/select-property/statistics"
            element={
              pendingSession || (loggedIn && STAFF_ROLES.includes(userRole)
                && loginMode !== "tenant"
                ? <StatisticsPage />
                : <Navigate to="/manager-login" />)
            }
          />

          {/* ================= DASHBOARD ================= */}
          <Route
            path="/dashboard"
            element={requireStaffWithProperty(<Dashboard />)}
          />
          <Route
            path="/automation-exceptions"
            element={requireStaffWithProperty(<AutomationExceptionsDashboard />)}
          />
          <Route
            path="/e-transfer-automation"
            element={requireStaffWithProperty(<ETransferAutomationPage />, { key: "rentCollection", min: "read" })}
          />

          {/* ================= NEW NAV HUBS (TASK-FIRST) ================= */}
          <Route
            path="/inspections-hub"
            element={requireStaffWithProperty(<InspectionsHub />, { key: "inspections", min: "readOrWrite" })}
          />
          <Route
            path="/inspections/spec/:slug"
            element={requireStaffWithProperty(<InspectionSpecRouter />, { key: "inspections", min: "write" })}
          />

          <Route
            path="/leases"
            element={requireStaffWithProperty(<LeasesHub />)}
          />
          <Route
            path="/leases/spec/:slug"
            element={requireStaffWithProperty(<LeasesSpecRouter />, { key: "leasesCreate", min: "write" })}
          />

          <Route
            path="/work-orders-hub"
            element={requireStaffWithProperty(<WorkOrdersHub />, { key: "workOrders", min: "readOrWrite" })}
          />
          <Route
            path="/work-orders/spec/:slug"
            element={requireStaffWithProperty(<WorkOrdersSpecRouter />, { key: "workOrders", min: "write" })}
          />

          <Route
            path="/financials"
            element={requireStaffWithProperty(<FinancialsHub />)}
          />
          <Route
            path="/financials/spec/:slug"
            element={requireStaffWithProperty(<FinancialsSpecRouter />, { key: "expenses", min: "read" })}
          />
          <Route
            path="/entities"
            element={requireStaffWithProperty(<EntityFinancialsPage />)}
          />
          <Route
            path="/entities/:entityId"
            element={requireStaffWithProperty(<EntityFinancialsPage />)}
          />

          <Route
            path="/reports"
            element={requireStaffWithProperty(<ReportsHub />)}
          />
          <Route
            path="/reports/rent-roll"
            element={requireStaffWithProperty(<RentRollReport />)}
          />
          <Route
            path="/reports/rent-roll-t12"
            element={requireStaffWithProperty(<RentRollT12Report />)}
          />
          <Route
            path="/reports/spec/:slug"
            element={requireStaffWithProperty(<ReportsSpecRouter />, { key: "reports", min: "read" })}
          />
          <Route
            path="/marketing"
            element={requireStaffWithProperty(<MarketingHub />)}
          />

          <Route
            path="/notices"
            element={requireStaffWithProperty(<NoticesHub />, { key: "notices", min: "readOrWrite" })}
          />
          <Route
            path="/assistant"
            element={requireStaffWithProperty(<PropertyAssistant />)}
          />
          <Route
            path="/documents"
            element={requireStaffWithProperty(<DocumentsPage />)}
          />
          <Route
            path="/tasks"
            element={requireStaffWithProperty(<TasksPage />, { key: "tasks", min: "readOrWrite" })}
          />
          <Route
            path="/tasks/:taskId"
            element={requireStaffWithProperty(<TasksPage />, { key: "tasks", min: "read" })}
          />
          <Route
            path="/tenant-messages"
            element={requireStaffWithProperty(<TenantMessagesInbox />)}
          />

          <Route
            path="/property-settings"
            element={requireStaffWithProperty(<PropertySettingsHub />, { key: "editProperty", min: "write" })}
          />
          <Route
            path="/property-settings/tenant-information"
            element={requireStaffWithProperty(<PropertyTenantInformation />, { key: "editProperty", min: "write" })}
          />
          <Route
            path="/property-settings/default-lease-setup"
            element={requireStaffWithProperty(<PropertyDefaultLeaseSetup />, { key: "editProperty", min: "write" })}
          />
          <Route
            path="/property-settings/users"
            element={requireStaffWithProperty(<PropertyUsersSettings />, { key: "editProperty", min: "write" })}
          />
          <Route
            path="/property-settings/auto-email"
            element={requireStaffWithProperty(<AutoEmailSettings />, { key: "editProperty", min: "write" })}
          />
          <Route
            path="/property-settings/suites"
            element={requireStaffWithProperty(<PropertySettingsSuites />, { key: "editProperty", min: "write" })}
          />
          <Route
            path="/property-settings/inspection-visibility"
            element={requireStaffWithProperty(<PropertyInspectionVisibilitySettings />, { key: "editProperty", min: "write" })}
          />
          <Route
            path="/property-settings/suites/:suiteId"
            element={requireStaffWithProperty(<SuiteEditor />)}
          />
          <Route
            path="/property-settings/spec/:slug"
            element={requireStaffWithProperty(<PropertySettingsSpecRouter />, { key: "editProperty", min: "write" })}
          />

          {/* ================= SUITE ALIASES ================= */}
          <Route
            path="/rename-suites"
            element={requireStaffWithProperty(<RenameSuites />, { key: "editProperty", min: "write" })}
          />

          {/* ================= INSPECTIONS ================= */}
          <Route
            path="/monthly"
            element={requireStaffWithProperty(<InspectionForm />, { key: "inspections", min: "write" })}
          />
          <Route
            path="/quarterly"
            element={requireStaffWithProperty(<QuarterlyInspectionForm />, { key: "inspections", min: "write" })}
          />
          <Route
            path="/annual"
            element={requireStaffWithProperty(<AnnualInspectionForm />, { key: "inspections", min: "write" })}
          />
          <Route
            path="/spring"
            element={requireStaffWithProperty(<SpringChecklist />, { key: "inspections", min: "write" })}
          />
          <Route
            path="/fall"
            element={requireStaffWithProperty(<FallChecklist />, { key: "inspections", min: "write" })}
          />
          <Route
            path="/moveout"
            element={requireStaffWithProperty(<MoveOutChecklist />, { key: "inspections", min: "write" })}
          />
          <Route
            path="/movein"
            element={requireStaffWithProperty(<MoveInChecklist />, { key: "inspections", min: "write" })}
          />

          {/* ================= LEASES ================= */}
          <Route
            path="/monthlylease"
            element={requireStaffWithProperty(<MonthlyLeaseAgreement />, { key: "leasesCreate", min: "write" })}
          />
          <Route
            path="/fixedlease"
            element={requireStaffWithProperty(<FixedLeaseAgreement />, { key: "leasesCreate", min: "write" })}
          />
          <Route
            path="/lease-overview"
            element={requireStaffWithProperty(<LeaseOverview />, { key: "leasesView", min: "read" })}
          />
          <Route
            path="/leases/import"
            element={requireStaffWithProperty(<LeaseImportPage />, { key: "leasesCreate", min: "write" })}
          />



          {/* ================= INSPECTION TRACKER ================= */}
          <Route
            path="/inspections"
            element={requireStaffWithProperty(<InspectionTracker />, { key: "inspections", min: "read" })}
          />

          {/* ================= WORK ORDERS ================= */}
          <Route
            path="/work-orders"
            element={requireStaffWithProperty(<WorkOrdersList />, { key: "workOrders", min: "read" })}
          />
          <Route
            path="/work-orders/create"
            element={requireStaffWithProperty(<CreateWorkOrderPage />, { key: "workOrders", min: "write" })}
          />
          <Route
            path="/work-orders/:workOrderId"
            element={requireStaffWithProperty(<WorkOrderDetail />, { key: "workOrders", min: "read" })}
          />
          <Route
            path="/vendors"
            element={requireStaffWithProperty(<VendorsPage />, { key: "workOrders", min: "write" })}
          />

          {/* ================= FINANCIAL ================= */}
          <Route
            path="/rent-tracker"
            element={requireStaffWithProperty(<RentCollectionDashboard />, { key: "rentCollection", min: "read" })}
          />
          <Route
            path="/expenses"
            element={requireStaffWithProperty(<ExpenseDashboard />, { key: "expenses", min: "read" })}
          />
          <Route
            path="/revenue"
            element={requireStaffWithProperty(<RevenueDashboard />, { key: "revenue", min: "read" })}
          />
          <Route
            path="/entity-financials"
            element={requireStaffWithProperty(<Navigate to="/entities" replace />)}
          />
          <Route
            path="/yearly-summary"
            element={requireStaffWithProperty(<YearlySummaryDashboard />, { key: "reports", min: "read" })}
          />

          {/* ================= ASSESSMENT ================= */}
          <Route
            path="/assessment"
            element={requireStaffWithProperty(<Assessment />)}
          />
          <Route
            path="/Assessment"
            element={requireStaffWithProperty(<Assessment />)}
          />

          {/* ================= SIGNING ================= */}
          <Route path="/sign/:documentId" element={<DocumentSignPage />} />
          <Route path="/ontario-sign/:leaseId" element={<OntarioLeaseSignPage />} />
          <Route path="/manitoba-sign/:leaseId" element={<ManitobaLeaseSignPage />} />
          <Route path="/lease-sign/:leaseId" element={<LeaseSignPage />} />
          <Route
            path="/Ontario-Lease"
            element={requireStaffWithProperty(<OntarioLease />, { key: "leasesCreate", min: "write" })}
          />
          <Route
            path="/Manitoba-Lease"
            element={requireStaffWithProperty(<ManitobaLease />, { key: "leasesCreate", min: "write" })}
          />
          <Route
            path="/Alberta-Lease"
            element={requireStaffWithProperty(<AlbertaLease />, { key: "leasesCreate", min: "write" })}
          />

          {/* ================= METRICS ================= */}
          <Route
            path="/metrics"
            element={requireStaffWithProperty(<MetricsDashboard />, { key: "reports", min: "read" })}
          />

          {/* ================= TENANT ================= */}
          <Route
            path="/tenant-dashboard"
            element={
              pendingSession || (loggedIn && hasTenantAccess
                ? <TenantDashboard />
                : <Navigate to="/" />)
            }
          />
          <Route path="*" element={<PageState title="Page not found" action={<Link className="app-button-primary" to={loggedIn ? "/dashboard" : "/"}>Return to {loggedIn ? "dashboard" : "home"}</Link>}>This address is unavailable. Use the navigation to find the page you need.</PageState>} />
          </Routes>
          </Suspense>
          </AppErrorBoundary>
          </main>
          <PropertyAssistantDock enabled={assistantDockEnabled} />
        </div>
      </PageAssistantProvider>
    </Router>
  );
}

export default App;
